import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import type {
  panel,
  panelPatch,
} from "types/api-types/panels/panel/panel.types";
import type {
  yardInventoryItemsData,
} from "types/api-types/yard/yard-inventory-items/yard-inventory-items.types";
import type {
  yardLocations,
  yardLocationsData,
} from "types/api-types/yard/yard-locations/yard-locations.types";

interface YardLocation {
  aisle: string;
  row: string;
  position?: string;
}

interface InventoryData {
  panelId: number | null;
  productId: number | null;
  location: YardLocation;
  lotNumber: string;
  qrCode: string;
  barcode: string;
  notes: string;
  qcStatus: "pending" | "passed" | "failed" | "rework";
}

class YardInventoryWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;
  selectedPanels: panel[] = [];

  inventoryData: InventoryData = {
    panelId: null,
    productId: null,
    location: {
      aisle: "",
      row: "",
      position: "",
    },
    lotNumber: "",
    qrCode: "",
    barcode: "",
    notes: "",
    qcStatus: "pending",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateInventoryData = <K extends keyof InventoryData>(
    field: K,
    value: InventoryData[K]
  ) => {
    this.inventoryData[field] = value;
  };

  updateLocation = (field: keyof YardLocation, value: string) => {
    this.inventoryData.location[field] = value;
  };

  addPanel = () => {
    if (!this.inventoryData.panelId) return false;

    const panelStore = this.rootStore.panelStore as ServiceStore;
    const panel = panelStore?.entities.find(
      (p) => p.id === this.inventoryData.panelId
    );

    if (!panel) return false;

    if (this.selectedPanels.find((p) => p.id === panel.id)) {
      this.error = "Panel already added";
      return false;
    }

    runInAction(() => {
      this.selectedPanels.push(panel as unknown as panel);
      this.inventoryData.panelId = null;
    });

    return true;
  };

  removePanel = (panelId: number) => {
    this.selectedPanels = this.selectedPanels.filter((p) => p.id !== panelId);
  };

  generateQRCode = (): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `QR-${timestamp}-${random}`.toUpperCase();
  };

  generateBarcode = (): string => {
    const timestamp = Date.now();
    return `BC${timestamp}`;
  };

  generateLotNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const sequence = Math.floor(Math.random() * 999) + 1;
    return `LOT-${year}${month}${day}-${sequence.toString().padStart(3, "0")}`;
  };

  // Async actions
  assignLocation = async () => {
    this.loading = true;
    this.error = null;

    try {
      const yardLocationsStore = this.rootStore
        .yardLocationsStore as ServiceStore;
      const yardInventoryItemsStore = this.rootStore
        .yardInventoryItemsStore as ServiceStore;
      const panelStore = this.rootStore.panelStore as ServiceStore;

      // Find or create yard location
      const locationName = `${this.inventoryData.location.aisle}-${
        this.inventoryData.location.row
      }${
        this.inventoryData.location.position
          ? `-${this.inventoryData.location.position}`
          : ""
      }`;
      let yardLocation = yardLocationsStore?.entities.find(
        (loc) => loc.name === locationName
      ) as unknown as yardLocations | undefined;

      if (!yardLocation) {
        // Create new yard location
        const locationData: yardLocationsData = {
          name: `${this.inventoryData.location.aisle}-${
            this.inventoryData.location.row
          }${
            this.inventoryData.location.position
              ? `-${this.inventoryData.location.position}`
              : ""
          }`,
          locationType: "storage_area",
          capacity: 10,
          description: `Aisle: ${this.inventoryData.location.aisle}, Row: ${
            this.inventoryData.location.row
          }${
            this.inventoryData.location.position
              ? `, Position: ${this.inventoryData.location.position}`
              : ""
          }`,
        };
        const created = await yardLocationsStore?.create(
          locationData as unknown as Parameters<
            typeof yardLocationsStore.create
          >[0]
        );
        yardLocation = (
          Array.isArray(created) ? created[0] : created
        ) as unknown as yardLocations;
      }

      if (!yardLocation) {
        throw new Error("Failed to create yard location");
      }

      // Create inventory items for each selected panel
      const lotNumber =
        this.inventoryData.lotNumber || this.generateLotNumber();

      for (const panel of this.selectedPanels) {
        // The panel entity carries no productId/qcStatus (see panel.schema.ts —
        // it has productType and productionStatus), so those values come from
        // the wizard's own inventoryData, with a default.
        const inventoryData: yardInventoryItemsData = {
          productId: this.inventoryData.productId || 1,
          lotNumber: lotNumber || "",
          qcStatus: this.inventoryData.qcStatus || "pending",
          currentLocationId: yardLocation.id,
          quantity: 1,
          dateAdded: new Date().toISOString(),
          notes: this.inventoryData.notes || "",
        };

        await yardInventoryItemsStore?.create(
          inventoryData as unknown as Parameters<
            typeof yardInventoryItemsStore.create
          >[0]
        );

        // Update panel with yard location using existing fields
        const panelUpdate: panelPatch = {
          yardRow: this.inventoryData.location.aisle, // Using aisle as row
          yardColumn: `${this.inventoryData.location.row}${
            this.inventoryData.location.position
              ? `-${this.inventoryData.location.position}`
              : ""
          }`, // Combining row and position as column
          // productionStatus: "completed" as const, // Mark as completed when in yard
        };
        await panelStore?.patch(
          panel.id,
          panelUpdate as unknown as Parameters<typeof panelStore.patch>[1]
        );
      }

      runInAction(() => {
        this.success = `${this.selectedPanels.length} items successfully added to yard inventory`;
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to assign location";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  printLabels = () => {
    // TODO: Implement label printing
    window.print();
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.selectedPanels = [];
    this.inventoryData = {
      panelId: null,
      productId: null,
      location: {
        aisle: "",
        row: "",
        position: "",
      },
      lotNumber: "",
      qrCode: "",
      barcode: "",
      notes: "",
      qcStatus: "pending",
    };
  };

  // Computed values
  get panels(): panel[] {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    const today = new Date().toISOString().split("T")[0]; // Get today's date in YYYY-MM-DD format
    // Filter panels that have been cast (castingDate not 1970-01-01, and not in future) and not yet in yard
    return (
      (panelStore?.entities.filter(
        (p) =>
          p.castingDate &&
          p.castingDate !== "1970-01-01" &&
          p.castingDate <= today &&
          !p.yardRow && // Check both yardRow and yardColumn
          !p.yardColumn
      ) as unknown as panel[]) || []
    );
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get yardLocations() {
    const yardLocationsStore = this.rootStore
      .yardLocationsStore as ServiceStore;
    return yardLocationsStore?.entities || [];
  }

  get aisles() {
    // Extract aisles from location names (format: AISLE-ROW-POSITION)
    const aisleSet = new Set<string>();
    this.yardLocations.forEach((loc: any) => {
      const parts = loc.name?.split("-");
      if (parts && parts[0]) {
        aisleSet.add(parts[0]);
      }
    });
    return [...aisleSet].filter(Boolean);
  }

  get rows() {
    // Extract rows from location names (format: AISLE-ROW-POSITION)
    const rowSet = new Set<string>();
    this.yardLocations.forEach((loc: any) => {
      const parts = loc.name?.split("-");
      if (parts && parts[1]) {
        rowSet.add(parts[1]);
      }
    });
    return [...rowSet].filter(Boolean);
  }

  get locationString() {
    const { aisle, row, position } = this.inventoryData.location;
    return `${aisle || "[Aisle]"}-${row || "[Row]"}${
      position ? `-${position}` : ""
    }`;
  }

  get canProceedFromStep1() {
    return this.selectedPanels.length > 0;
  }

  get canProceedFromStep2() {
    return (
      !!this.inventoryData.location.aisle && !!this.inventoryData.location.row
    );
  }

  getProductForPanel(panel: any) {
    return this.products.find((p: any) => p.id === panel?.productId);
  }

  getJobForPanel(panel: any) {
    return this.jobs.find((j: any) => j.id === panel?.jobId);
  }
}

export default YardInventoryWizardStore;
